// `/admin/webhooks/inbox` (croqui `screens/admin/webhooks-recebidos.tsx`). Foco: **a
// tabela de eventos recebidos**.
//
// Esta é a rota-PAI: ela desenha a lista e, por cima, o `RouteDrawer` com o `<Outlet/>`
// da rota `$id` (`spec/24` §1 decisão 5). Deep link em `.../<uuid>` monta a lista embaixo
// e o drawer aberto; fechar volta para cá preservando o `search`. O schema mora aqui (e
// não no `index`) porque o `$id` é filho desta rota e herda o mesmo `search`.
//
// Os filtros vivem na query string (`spec/17` §3), não em `useState`. A busca textual é a
// exceção deliberada: é filtro de CLIENTE sobre o que já foi carregado (`GET
// /admin/v1/webhook-inbox` não tem `q`), e pô-la na URL prometeria uma busca no servidor
// que não existe.

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { acquirersQueryOptions } from '../api/queries/admin-acquirers.ts';
import { inboxListOptions } from '../api/queries/admin-webhooks.ts';
import { PageHeader } from '../components/kit/page-header.tsx';
import { RouteDrawer } from '../components/kit/route-drawer.tsx';
import { useInboxDrawerHeader } from '../features/admin-webhooks/inbox-detail.tsx';
import { InboxScreen } from '../features/admin-webhooks/inbox-screen.tsx';
import {
  INBOX_TAB_IDS,
  type InboxTab,
  matchesInboxSearch,
} from '../features/admin-webhooks/model.ts';

/**
 * A aba é o `state` da URL: os links que já existiam (`?state=unprocessed`) continuam
 * valendo, e `all` é um valor do search, não a ausência dele. Query string tolerante a
 * lixo: `?state=banana` cai em `all` (`catch` do Zod).
 */
const searchSchema = z.object({
  state: z.enum(INBOX_TAB_IDS).default('all').catch('all'),
  acquirer_id: z.string().min(1).optional().catch(undefined),
});

function AdminInbox() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');

  const filters = {
    ...(search.state === 'all' ? {} : { state: search.state }),
    ...(search.acquirer_id === undefined ? {} : { acquirer_id: search.acquirer_id }),
  };

  const list = useInfiniteQuery(inboxListOptions(filters));
  // A lista de adquirentes é o `Select` do croqui. `GET /admin/v1/acquirers` não exige
  // step-up (`ADMIN_STEP_UP_ROUTES` só lista as escritas) e devolve os reais, Dotfy e
  // Fake, nunca os cinco nomes que o croqui inventava.
  const acquirers = useQuery(acquirersQueryOptions());
  const loaded = list.data?.pages.flatMap((page) => page.data) ?? [];
  const rows = loaded.filter((row) => matchesInboxSearch(row, term));

  // A rota `$id` é filha desta: se ela casa, o detalhe está aberto por cima da lista.
  const detail = useMatch({ from: '/admin/webhooks/inbox/$id', shouldThrow: false });
  const header = useInboxDrawerHeader(detail?.params.id);

  const go = (next: { state?: InboxTab; acquirer_id?: string | undefined }): void => {
    const state = next.state ?? search.state;
    const acquirerId = 'acquirer_id' in next ? next.acquirer_id : search.acquirer_id;
    void navigate({
      to: '/admin/webhooks/inbox',
      search: { state, ...(acquirerId === undefined ? {} : { acquirer_id: acquirerId }) },
    });
  };

  return (
    <>
      <PageHeader
        title="Webhooks recebidos"
        sub="Eventos crus dos adquirentes, antes de virarem efeito"
        path="/admin/v1/webhook-inbox"
        size="lg"
      />

      <InboxScreen
        rows={rows}
        tab={search.state}
        onTabChange={(next) => {
          go({ state: next });
        }}
        acquirers={acquirers.data ?? []}
        acquirerId={search.acquirer_id}
        onAcquirerChange={(id) => {
          go({ acquirer_id: id });
        }}
        search={term}
        onSearchChange={setTerm}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => {
          void list.refetch();
        }}
        hasMore={list.hasNextPage}
        isLoadingMore={list.isFetchingNextPage}
        onLoadMore={() => {
          void list.fetchNextPage();
        }}
        onSelect={(row) => {
          // `search` explícito: o filtro da lista tem de sobreviver à abertura do drawer.
          void navigate({ to: '/admin/webhooks/inbox/$id', params: { id: row.id }, search });
        }}
        {...(detail === undefined ? {} : { selectedId: detail.params.id })}
        onClearFilters={() => {
          setTerm('');
          go({ state: 'all', acquirer_id: undefined });
        }}
      />

      <RouteDrawer
        open={detail !== undefined}
        width={460}
        onClose={() => {
          void navigate({ to: '/admin/webhooks/inbox', search });
        }}
        title={header.title}
        {...(header.sub === undefined ? {} : { sub: header.sub })}
      >
        <Outlet />
      </RouteDrawer>
    </>
  );
}

export const Route = createFileRoute('/admin/webhooks/inbox')({
  validateSearch: searchSchema,
  component: AdminInbox,
});
